import {
    Box,
    Chip,
    Typography,
    Avatar,
    Dropdown,
    Menu,
    MenuButton,
    MenuItem,
    Divider,
    IconButton,
} from '@mui/joy';
import VerifiedBadge from '../../ui/VerifiedBadge';
import {
    Heart,
    DotsThreeVertical,
    PencilSimple,
    Trash,
    Flag,
    X,
    Check,
    Play,
    ArrowBendUpLeft,
} from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import FormatDateDistanceToNow from '../FormatDateDistanceToNow/FormatDateDistanceToNow';
import ModalConfirmDelete from '../ModalConfirmDelete/ModalConfirmDelete';
import ModalComplain from '../ModalComplain/ModalComplain';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../../utils/apiClient';
import { mediaUrl } from '../../../utils/mediaUrl';

const isVideoPath = (src) => /\.(mp4|webm|ogg|mov)$/i.test(src ?? '');

const attachmentUrl = (src) => mediaUrl(src) ?? '';

/**
 * Фото и видео, прикреплённые к комментарию.
 *
 * Полосой, а не сеткой: вложений тут максимум пять, и они не должны спорить с
 * текстом за внимание. Клик открывает просмотрщик СО ВСЕМИ вложениями
 * комментария — иначе из него нельзя было бы листать к соседним.
 */
function CommentAttachments({ attachments = [] }) {
    const openViewer = useCallback((index) => {
        Fancybox.show(
            attachments.map((src) => ({
                src: attachmentUrl(src),
                type: isVideoPath(src) ? 'video' : 'image',
            })),
            {
                startIndex: index,
                Carousel: { infinite: false },
                Images: { initialSize: 'cover' },
            }
        );
    }, [attachments]);

    if (!Array.isArray(attachments) || attachments.length === 0) return null;

    return (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            {attachments.map((src, index) => (
                <Box
                    key={`${src}-${index}`}
                    onClick={() => openViewer(index)}
                    sx={{
                        position: 'relative',
                        width: 96,
                        height: 96,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        backgroundColor: 'background.level1',
                    }}
                >
                    {isVideoPath(src) ? (
                        <>
                            <Box
                                component="video"
                                src={attachmentUrl(src)}
                                preload="metadata"
                                muted
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.55)',
                                }}
                            >
                                <Play size={16} weight="fill" color="#fff" />
                            </Box>
                        </>
                    ) : (
                        <Box
                            component="img"
                            src={attachmentUrl(src)}
                            alt=""
                            loading="lazy"
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    )}
                </Box>
            ))}
        </Box>
    );
}

function CommentLike({ likes = 0, liked = false, onToggle = () => { } }) {
    const isAuth = !!localStorage.getItem('accessToken');

    return (
        <Box
            onClick={isAuth ? onToggle : undefined}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                mt: 0.75,
                borderRadius: '100px',
                cursor: isAuth ? 'pointer' : 'default',
                width: 'fit-content',
                '&:hover': {
                    backgroundColor: isAuth ? 'neutral.softHoverBg' : 'transparent',
                },
            }}
        >
            <Heart
                size={16}
                weight={liked ? 'fill' : 'regular'}
                color={liked ? '#ff0000' : 'currentColor'}
            />
            {!!likes && likes > 0 && (
                <Typography level="body-xs">{likes}</Typography>
            )}
        </Box>
    );
}

/**
 * Цитата комментария, на который отвечают.
 *
 * Клик уводит к самому комментарию: в длинной ветке иначе непонятно, о чём
 * речь, а разворачивать цитату целиком — значит дублировать текст.
 */
function ReplyQuote({ replyTo }) {
    if (!replyTo) return null;

    const name = [replyTo.user?.firstName, replyTo.user?.lastName]
        .filter(Boolean).join(' ') || 'Пользователь';

    const scrollToParent = () => {
        const el = document.getElementById(`comment-${replyTo.id}`);
        if (!el) return;

        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.animate?.(
            [
                { backgroundColor: 'transparent' },
                { backgroundColor: 'var(--joy-palette-primary-softBg)' },
                { backgroundColor: 'transparent' },
            ],
            { duration: 1600, easing: 'ease-in-out' }
        );
    };

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={scrollToParent}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    scrollToParent();
                }
            }}
            sx={{
                mt: 0.75,
                pl: 1,
                borderLeft: '2px solid',
                borderColor: 'primary.500',
                cursor: 'pointer',
                outline: 'none',
                '&:focus-visible': { boxShadow: '0 0 0 2px var(--joy-palette-primary-outlinedBorder)' },
            }}
        >
            <Typography level="body-xs" textColor="primary.500" fontWeight={600} noWrap>
                {name}
            </Typography>
            {!!replyTo.content && (
                <Typography
                    level="body-xs"
                    color="neutral"
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {replyTo.content}
                </Typography>
            )}
        </Box>
    );
}

const stripHtml = (html) => {
    if (typeof html !== 'string') return '';
    const el = document.createElement('div');
    el.innerHTML = html;
    return el.textContent || el.innerText || '';
};

function CommentItem({ comment, onDelete = () => { }, onEdit = () => { }, onReply = null }) {
    const [isEditing, setIsEditing] = useState(false);
    // Редактирование на сайте — простым текстом (rich-ввод только в приложении).
    const [editContent, setEditContent] = useState(stripHtml(comment.content));
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openComplain, setOpenComplain] = useState(false);
    const [liked, setLiked] = useState(!!comment.liked);
    const [likes, setLikes] = useState(comment.countLikes ?? 0);
    const contentRef = useRef(null);

    const isAuth = !!localStorage.getItem('accessToken');

    const toggleLike = async () => {
        const wasLiked = liked;
        const base = likes;
        const nextLiked = !wasLiked;
        setLiked(nextLiked);
        setLikes(Math.max(0, base + (nextLiked ? 1 : -1)));
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) throw new Error('Требуется авторизация');
            const res = await apiFetch(`/api/post/comments/like`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: comment.id }),
            });
            if (!res.ok) throw new Error('Ошибка лайка');
            const data = await res.json();
            setLiked(!!data.liked);
            setLikes(data.countLikes ?? Math.max(0, base + (nextLiked ? 1 : -1)));
        } catch {
            setLiked(wasLiked);
            setLikes(base);
        }
    };

    const startEditing = () => {
        setIsEditing(true);
        setTimeout(() => {
            if (!contentRef.current) return;
            contentRef.current.textContent = editContent;
            contentRef.current.focus();

            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(contentRef.current);
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }, 0);
    };

    const cancelEditing = () => {
        setEditContent(stripHtml(comment.content));
        setIsEditing(false);
    };

    const saveEditing = async () => {
        setIsEditing(false);

        try {
            const accessToken = localStorage.getItem('accessToken')
            if (!accessToken) throw new Error('Требуется авторизация')

            const res = await apiFetch(`/api/post/comments`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: comment.id,
                    content: editContent
                }),
            })

            if (!res.ok) throw new Error('Ошибка при редактировании комментария');
            const data = await res.json();

            if (data?.success) {
                onEdit(comment.id, editContent);
            }
        } catch (err) {

        }
    };

    const handlerOnClickDelete = () => setOpenDeleteModal(true)

    const handlerDelete = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken')
            if (!accessToken) throw new Error('Требуется авторизация')

            const res = await apiFetch(`/api/post/comments?id=${comment.id}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            })

            if (!res.ok) throw new Error('Ошибка при редактировании комментария');
            const data = await res.json();

            if (data?.success) {
                onDelete(comment.id);
            }
        } catch (err) {

        }
    }

    useEffect(() => {
        setEditContent(stripHtml(comment.content));
    }, [comment.content]);

    useEffect(() => {
        setLiked(!!comment.liked);
        setLikes(comment.countLikes ?? 0);
    }, [comment.liked, comment.countLikes]);

    // Отвечать можно только на чужие комментарии — то же правило, что и на
    // сервере, иначе кнопка вела бы к отказу в ответе.
    const canReply = Boolean(onReply) && isAuth && !comment.myComment;

    return (
        <Box
            id={`comment-${comment.id}`}
            sx={{ display: "flex", alignItems: 'start', gap: 1.5, mb: 3, borderRadius: '12px' }}
        >
            <Box component="a" href={`/show/id/${comment?.user?.id}`}>
                <Avatar src={mediaUrl(comment.user?.avatar)} size="md" sx={{ mt: 0.25 }} />
            </Box>

            <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Box
                        component="a"
                        href={`/show/id/${comment?.user?.id}`}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'start',
                            alignItems: 'start',
                            textDecoration: "none !important"
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                            <Typography level="body-sm" fontWeight={500} noWrap sx={{ minWidth: 0 }}>
                                {comment.user?.firstName} {comment.user?.lastName}
                            </Typography>
                            {!!comment.user?.verified && <VerifiedBadge size={13} type={comment.user?.verified} />}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography level="body-xs" color="neutral">
                                <FormatDateDistanceToNow dateString={comment.created_at} />
                            </Typography>

                            {/* Скрытый комментарий виден только своему автору —
                                и он должен понимать, что остальные его пока
                                не видят. */}
                            {Number(comment.status) === 0 && (
                                <Chip size="sm" variant="soft" color="warning">
                                    На модерации
                                </Chip>
                            )}
                        </Box>
                    </Box>

                    {!isEditing && isAuth && (
                        <Dropdown>
                            <MenuButton
                                slots={{ root: Box }}
                                slotProps={{
                                    root: {
                                        sx: {
                                            width: 32,
                                            height: 32,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "50%",
                                            cursor: "pointer",
                                            color: "neutral.500",
                                            "&:hover": { backgroundColor: "neutral.softHoverBg" },
                                        },
                                    },
                                }}
                            >
                                <DotsThreeVertical size={18} />
                            </MenuButton>
                            <Menu placement="bottom-end" variant="plain" sx={{ borderRadius: 12 }}>
                                {comment.myComment ? (
                                    <>
                                        <MenuItem sx={{ py: "6px", px: "10px" }} onClick={startEditing}>
                                            <PencilSimple size={18} /> Редактировать
                                        </MenuItem>
                                        <Divider sx={{ my: 0.5 }} />
                                        <MenuItem sx={{ py: "6px", px: "10px" }} color="danger" onClick={handlerOnClickDelete}>
                                            <Trash size={18} /> Удалить
                                        </MenuItem>
                                    </>
                                ) : (
                                    <MenuItem sx={{ py: "6px", px: "10px" }} color="danger" onClick={() => setOpenComplain(true)}>
                                        <Flag size={18} /> Пожаловаться
                                    </MenuItem>
                                )}
                            </Menu>
                        </Dropdown>
                    )}

                    {isEditing && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton size="sm" onClick={saveEditing} sx={{ borderRadius: '50px' }}>
                                <Check size={18} color="#28a745" />
                            </IconButton>
                            <IconButton size="sm" onClick={cancelEditing} sx={{ borderRadius: '50px' }}>
                                <X size={18} color="#d32f2f" />
                            </IconButton>
                        </Box>
                    )}
                </Box>

                <ReplyQuote replyTo={comment.replyTo} />

                {isEditing ? (
                    <Typography
                        key="editing"
                        ref={contentRef}
                        contentEditable
                        suppressContentEditableWarning
                        sx={{
                            mt: 0.75,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            display: "block",
                            width: "100%",
                            maxWidth: "100%",
                            border: "2px solid #8884ff",
                            borderRadius: "6px",
                            px: 0.5,
                        }}
                        onInput={(e) => setEditContent(e.currentTarget.textContent)}
                    />
                ) : (
                    <Typography
                        key="viewing"
                        component="div"
                        sx={{
                            mt: 0.75,
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            display: "block",
                            width: "100%",
                            maxWidth: "100%",
                            '& p': { m: 0, mb: 0.5 },
                            '& h3': { fontSize: '1.25rem', fontWeight: 'bold', my: 1 },
                            '& ul, & ol': { pl: 3, my: 0 },
                            '& a': { color: 'primary.500', textDecoration: 'none' },
                        }}
                    >
                        {/* dangerouslySetInnerHTML нельзя вешать прямо на Joy Typography:
                            он рендерит root с массивом children, и React бросает #60
                            («Can only set one of children or dangerouslySetInnerHTML»). */}
                        <div dangerouslySetInnerHTML={{ __html: comment.content ?? '' }} />
                    </Typography>
                )}

                <CommentAttachments attachments={comment.attachments} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CommentLike likes={likes} liked={liked} onToggle={toggleLike} />

                    {canReply && (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => onReply(comment)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onReply(comment);
                                }
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.75,
                                py: 0.25,
                                mt: 0.75,
                                borderRadius: '100px',
                                cursor: 'pointer',
                                width: 'fit-content',
                                outline: 'none',
                                '&:hover': { backgroundColor: 'neutral.softHoverBg' },
                                '&:focus-visible': { boxShadow: '0 0 0 2px var(--joy-palette-primary-outlinedBorder)' },
                            }}
                        >
                            <ArrowBendUpLeft size={16} />
                            <Typography level="body-xs">Ответить</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
            <ModalConfirmDelete
                open={openDeleteModal}
                setOpen={setOpenDeleteModal}
                onDelete={handlerDelete}
                desc="Вы действительно хотите удалить комментарий? Это действие необратимо."
            />
            <ModalComplain
                open={openComplain}
                setOpen={setOpenComplain}
                complainFor={5}
                contentId={comment.id}
            />
        </Box>
    );
}

export function CommentsShow({ comments = [], newComments = [], onDelete, onEdit, onReply }) {
    return (
        <Box>
            <AnimatePresence initial={false}>
                {newComments.map((comment) => (
                    <motion.div
                        key={`new-${comment.id}`}
                        initial={{ opacity: 0, y: -8, filter: 'blur(4px)' }}
                        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, y: -4, filter: 'blur(4px)' }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                        <CommentItem comment={comment} onDelete={onDelete} onEdit={onEdit} onReply={onReply} />
                    </motion.div>
                ))}
            </AnimatePresence>

            <AnimatePresence initial={false}>
                {comments.map((comment) => (
                    <motion.div
                        key={`fetch-${comment.id}`}
                        initial={{ opacity: 0, y: -8, filter: 'blur(4px)' }}
                        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, y: -4, filter: 'blur(4px)' }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                        <CommentItem comment={comment} onDelete={onDelete} onEdit={onEdit} onReply={onReply} />
                    </motion.div>
                ))}
            </AnimatePresence>
        </Box>
    );
}
